import type { CSSProperties } from "react";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from "react";
import styled from "@emotion/styled";
import type { DocumentCardProps } from "src/ui/components/DocumentUploader/DocumentCard";
import DocumentCard from "src/ui/components/DocumentUploader/DocumentCard";
import clsx from "clsx";
import type FileType from "src/constants/fileType";
import BlockingLoadingOverlayController from "src/ui/components/BlockingLoadingOverlay/BlockingLoadingOverlayController";
import translate from "src/lib/translate";
import reportErrorSentry from "src/lib/reportErrorSentry";

const Wrap = styled.div`
  width: 100vw;
  transform: translateX(-50%);
  left: 50%;
  position: relative;
  max-width: 100vw;
  overflow: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none; /* Firefox */
  padding: 1rem 0;

  &::-webkit-scrollbar {
    display: none;
  }

  &[data-scrolling="true"] {
    max-width: min(1000px, calc(100vw));

    @media (min-width: 600px) {
      padding: 0;
      overflow: visible;
      max-width: min(1000px, calc(100vw - 5rem));
    }
  }
`;

const Container = styled.div`
  display: flex;
  width: calc(calc(var(--items, 1) * 100%) + 100vw);
  gap: 1rem;
  overflow: visible;

  > .document-card {
    flex: 1;
    scroll-snap-align: center;
    --width: min(400px, 80vw);
    width: var(--width);
    min-width: var(--width);
    max-width: var(--width);

    &:first-of-type {
      margin-left: 50vw;
    }
    &:last-of-type {
      margin-right: 50vw;
    }
  }

  &[data-scrolling="true"] {
    @media (min-width: 600px) {
      width: 100%;
    }
  }
`;

const Indicators = styled.div`
  display: flex;
  justify-content: center;
  margin: -0.5rem 0 1.5rem;

  &[data-scrolling="true"] {
    @media (min-width: 600px) {
      display: none;
    }
  }

  &[data-hide="true"] {
    display: none;
  }
`;

const Indicator = styled.div`
  width: 1rem;
  height: 1rem;
  position: relative;
  display: grid;
  place-items: center;
  cursor: pointer;

  &:after {
    content: "";
    position: absolute;
    background-color: var(--greys-light-gray, #e6e3db);
    transition: background-color var(--ease-time) var(--ease-type);
    width: 0.5rem;
    aspect-ratio: 1;
    border-radius: 50%;
  }

  &.active:after {
    background-color: var(--color-charcoal, #333);
  }
`;

export interface SingleFileConfig {
  initialFileId?: string;
  title?: string;
  tags?: string[];
  fileTypes?: string[];
  metaFileType?: FileType;
  namePrefix?: string;
  icon?: DocumentCardProps["icon"];
}

interface PropsConfig<T extends string> {
  fileTypes?: string[];
  files: Record<T, SingleFileConfig>;
}

// default T value is string
interface Props<T extends string> {
  config: PropsConfig<T>;
  onChange?: (values: Record<T, string>, valid: boolean) => void;
  onSelect?: (file: File, values: Record<T, string>) => void;
  onFileSelected?: () => void;
  display?: {
    ratio?: number;
    showSelectedTitles?: boolean;
    action?: DocumentCardProps["action"];
  };
  /**
   * If true, files will be stored locally and not uploaded immediately.
   * Call uploadDeferredFiles() method via ref to upload them later.
   */
  deferUpload?: boolean;
}

export interface DocumentUploaderRef {
  uploadDeferredFiles: () => Promise<Map<string, string>>;
}

const DocumentUploader = React.forwardRef<DocumentUploaderRef, Props<string>>(
  (props, ref) => {
    const { config, onChange, deferUpload } = props;

    const documentValues = useRef<Record<string, string>>({});
    const scrollRef = useRef<HTMLDivElement>(null);
    const uploadFunctionsRef = useRef<
      Map<string, () => Promise<Map<string, string>>>
    >(new Map());
    const retryFunctionsRef = useRef<
      Map<string, (fileKey: string) => Promise<string | null>>
    >(new Map());
    const getStatusFunctionsRef = useRef<
      Map<
        string,
        (fileKey: string) => "pending" | "success" | "error" | undefined
      >
    >(new Map());

    const [activeIndex, setActiveIndex] = React.useState(0);
    const [uploadStatuses, setUploadStatuses] = React.useState<
      Record<string, "pending" | "success" | "error">
    >({});
    const isRetryingRef = useRef(false);

    // Expose uploadDeferredFiles method via ref
    useImperativeHandle(
      ref,
      () => ({
        uploadDeferredFiles: async () => {
          const allUploaded = new Map<string, string>();

          // Show loading overlay at the DocumentUploader level
          BlockingLoadingOverlayController.startLoading({ bg: "transparent" });

          try {
            // Create array of upload promises to run in parallel
            // Each picker will NOT show its own overlay (controlled by showLoadingOverlay param)
            const uploadPromises = Array.from(
              uploadFunctionsRef.current.entries()
            ).map(async ([docName, uploadFn]) => {
              const uploaded = await uploadFn();
              return { docName, uploaded };
            });

            // Wait for all uploads to complete in parallel
            const results = await Promise.all(uploadPromises);

            // Update statuses based on results
            const newStatuses: Record<string, "pending" | "success" | "error"> =
              {};
            let hasFailures = false;

            results.forEach(({ docName, uploaded }) => {
              // Merge results into allUploaded map - use docName as key
              for (const [, v] of uploaded.entries()) {
                allUploaded.set(docName, v);
              }

              // Check status for this document
              const getStatusFn = getStatusFunctionsRef.current.get(docName);
              if (getStatusFn) {
                // Get the file key from document values
                const fileKey =
                  documentValues.current[docName]?.replace("deferred-", "") ||
                  docName;
                const status = getStatusFn(fileKey);
                if (status) {
                  newStatuses[docName] = status;
                  if (status === "error") {
                    hasFailures = true;
                  }
                }
              }
            });

            // Update all statuses at once
            setUploadStatuses(newStatuses);

            // Show success or error based on results
            if (hasFailures) {
              BlockingLoadingOverlayController.loadingError({
                title: translate("error_unknown_error"),
                cancelText: "ok"
              });
            } else {
              BlockingLoadingOverlayController.loadingSuccess();
            }
          } catch (e) {
            reportErrorSentry(e);
            BlockingLoadingOverlayController.loadingError({
              title: translate("error_unknown_error")
            });
            throw e;
          }

          return allUploaded;
        }
      }),
      []
    );

    const documentList = useMemo<DocumentCardProps[]>(() => {
      const list: DocumentCardProps[] = [];
      for (const key in config.files) {
        const fileConfig = config.files[key];
        list.push({
          initialFileId: fileConfig.initialFileId,
          name: key,
          title: fileConfig.title,
          fileTypes: fileConfig.fileTypes,
          metaFileType: fileConfig.metaFileType,
          namePrefix: fileConfig.namePrefix,
          tags: fileConfig.tags,
          icon: fileConfig.icon
        });
      }
      return list;
    }, [config]);

    const documentListFileIds = useMemo(() => {
      const docValues = documentValues.current;
      return documentList.map((docProps) => {
        if (docProps.initialFileId) {
          documentValues.current[docProps.name] = docProps.initialFileId;

          return docProps.initialFileId;
        }

        return docProps.name in docValues ? docValues[docProps.name] : "";
      });
    }, [documentList]);

    const handleChange = (docProps: DocumentCardProps) => (fileId: string) => {
      documentValues.current[docProps.name] = fileId;

      // Check if all non-plus-button values are filled
      const requiredKeys = documentList
        .filter((doc) => doc.icon !== "plus")
        .map((doc) => doc.name);
      const valid = requiredKeys.every((key) => {
        return documentValues.current[key];
      });

      onChange?.({ ...documentValues.current }, valid);
    };

    const handleSelect = (docProps: DocumentCardProps) => (file: File) => {
      const docValues = documentValues.current;
      const withTmpValues = {
        ...docValues,
        [docProps.name]: file
      } as Record<string, string>;
      props.onSelect?.(file, withTmpValues);
      props.onFileSelected?.();
    };

    const handleUploadFunctionReady = useCallback(
      (docName: string) => (uploadFn: () => Promise<Map<string, string>>) => {
        uploadFunctionsRef.current.set(docName, uploadFn);
      },
      []
    );

    const handleRetryFunctionReady = useCallback(
      (docName: string) =>
        (retryFn: (fileKey: string) => Promise<string | null>) => {
          retryFunctionsRef.current.set(docName, retryFn);
        },
      []
    );

    const handleGetStatusFunctionReady = useCallback(
      (docName: string) =>
        (
          getStatusFn: (
            fileKey: string
          ) => "pending" | "success" | "error" | undefined
        ) => {
          getStatusFunctionsRef.current.set(docName, getStatusFn);
        },
      []
    );

    const handleRetry = useCallback(
      (docName: string) => async () => {
        const retryFn = retryFunctionsRef.current.get(docName);
        if (!retryFn) return;

        // Set flag to prevent auto-scroll during retry
        isRetryingRef.current = true;

        // Extract the file key from the docName (remove "deferred-" prefix if present)
        const fileKey =
          documentValues.current[docName]?.replace("deferred-", "") || docName;

        // Show loading for this specific file
        setUploadStatuses((prev) => ({ ...prev, [docName]: "pending" }));

        const result = await retryFn(fileKey);
        if (result) {
          // Update status to success
          setUploadStatuses((prev) => ({ ...prev, [docName]: "success" }));
          // Update the document value with the actual file ID
          documentValues.current[docName] = result;

          // Check if all non-plus-button files are now valid
          const requiredKeys = documentList
            .filter((doc) => doc.icon !== "plus")
            .map((doc) => doc.name);
          const allValid = requiredKeys.every((key) => {
            return (
              documentValues.current[key] &&
              !documentValues.current[key].startsWith("deferred-")
            );
          });

          // Notify parent with updated values
          onChange?.({ ...documentValues.current }, allValid);
        } else {
          // Retry failed, keep error status
          setUploadStatuses((prev) => ({ ...prev, [docName]: "error" }));
        }

        // Clear retry flag after a short delay to allow status to settle
        setTimeout(() => {
          isRetryingRef.current = false;
        }, 100);
      },
      [onChange, documentList]
    );

    // watch for scrolling
    React.useEffect(() => {
      const scrollElement = scrollRef.current;
      if (!scrollElement) return;

      const handleScroll = () => {
        const cards = scrollElement.querySelectorAll(".document-card");
        cards.forEach((card, index) => {
          const rect = card.getBoundingClientRect();
          const center = rect.left + rect.width / 2;
          const distance = Math.abs(center - window.innerWidth / 2);
          if (distance < 100) {
            setActiveIndex(index);
          }
        });
      };

      scrollElement.addEventListener("scroll", handleScroll, { passive: true });
      return () => {
        scrollElement.removeEventListener("scroll", handleScroll);
      };
    }, [scrollRef]);

    const scrollToIndex = (index: number) => {
      const scrollElement = scrollRef.current;
      if (!scrollElement) return;

      const cards = scrollElement.querySelectorAll(".document-card");
      const card = cards[index];
      if (!card) return;

      const rect = card.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const left = Math.round(center - scrollElement.clientWidth / 2);

      scrollElement.scrollBy({
        left,
        behavior: "smooth"
      });
    };

    useEffect(() => {
      // Don't auto-scroll during retry operations
      if (isRetryingRef.current) {
        return;
      }

      // Check if there are any failed uploads
      const firstFailedIndex = documentList.findIndex(
        (doc) => uploadStatuses[doc.name] === "error"
      );

      if (firstFailedIndex !== -1) {
        // Scroll to first failed file
        scrollToIndex(firstFailedIndex);
      } else {
        // No failures, scroll to first empty slot
        const firstEmptyIndex = documentListFileIds.findIndex((id) => !id);
        scrollToIndex(firstEmptyIndex);
      }
    }, [documentListFileIds, uploadStatuses, documentList]);

    return (
      <div>
        <Wrap ref={scrollRef}>
          <Container
            style={
              {
                "--items": documentList.length
              } as CSSProperties
            }
          >
            {documentList.map((docProps) => {
              return (
                <DocumentCard
                  {...docProps}
                  fileTypes={docProps.fileTypes ?? config.fileTypes}
                  key={docProps.name}
                  onChange={handleChange(docProps)}
                  onSelect={handleSelect(docProps)}
                  showSelectedTitles={props.display?.showSelectedTitles}
                  action={props.display?.action}
                  ratio={props.display?.ratio}
                  tags={docProps.tags}
                  deferUpload={deferUpload}
                  onUploadFunctionReady={handleUploadFunctionReady(
                    docProps.name
                  )}
                  onRetryFunctionReady={handleRetryFunctionReady(docProps.name)}
                  onGetStatusFunctionReady={handleGetStatusFunctionReady(
                    docProps.name
                  )}
                  uploadStatus={uploadStatuses[docProps.name]}
                  onRetry={handleRetry(docProps.name)}
                />
              );
            })}
          </Container>
        </Wrap>
        <Indicators data-hide={documentList.length <= 1}>
          {documentList.map((_, index) => (
            <Indicator
              key={index}
              onClick={() => {
                scrollToIndex(index);
              }}
              className={clsx({
                active: index === activeIndex
              })}
            />
          ))}
        </Indicators>
      </div>
    );
  }
);

export default DocumentUploader;
